import { useQuery } from '@tanstack/react-query';

import { queryClient } from '../components/queryClient';
import { useFleet } from '../hooks/useFleet';
import { useHasPermission } from '../hooks/useHasPermission';
import { getQueryStringValue, setQueryStringValue, useQueryString } from '../hooks/useQueryString';
import { Fleet } from '../interfaces';
import { FleetService } from '../services';
import { FLEET_ID_QUERY_PARAM_KEY } from '../constants/queryParams';

export const useFleetsQuery = () => {
  const hasPermission = useHasPermission('fleets:read:list');
  const queryKey = ['fleets-key'];

  const queryFn = () => FleetService.getAll();

  return useQuery({
    queryKey,
    queryFn,
    enabled: hasPermission,
  });
};

export const useFleetByUuidFromUrl = () => {
  const [fleetUuid, setFleetUuid] = useQueryString('fleetUuid');
  const hasPermission = useHasPermission('fleets:read:list');

  const { data: fleet, isLoading } = useQuery({
    queryKey: ['fleet', fleetUuid, 'details'],
    queryFn: () => FleetService.get(fleetUuid),
    enabled: hasPermission && !!fleetUuid,
  });

  return [isLoading, fleet, setFleetUuid] as const;
};

export const useFleetById = (fleetUUID: string) => {
  const hasPermission = useHasPermission('fleets:read:list');

  return useQuery({
    queryKey: ['fleet', fleetUUID, 'details'],
    queryFn: () => FleetService.get(fleetUUID),
    enabled: hasPermission && !!fleetUUID,
  });
};

export const refreshFleet = (fleetUuid: string) => {
  queryClient.invalidateQueries({ queryKey: ['fleet', fleetUuid, 'details'] });
};

export const forceRefreshFleets = () => {
  queryClient.invalidateQueries(['fleets-key']);
};

export const useLicensePlateTypes = (enabled: boolean) => {
  const fleet = useFleet();
  const queryKey = ['licensePlateTypes', fleet.uuid];

  const queryFn = () => FleetService.getLicensePlateTypes(fleet.uuid);

  return useQuery({
    queryKey,
    queryFn,
    enabled,
    cacheTime: 24 * 60 * 60 * 1000, // 24 hours
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
  });
};

export const useFleetOverride = () => {
  const queryKey = ['fleetOverride'];
  const fleetIdFromUrl = getQueryStringValue(FLEET_ID_QUERY_PARAM_KEY) ?? null;
  const queryFn = () => Promise.resolve<string | null>(null);

  const query = useQuery({
    initialData: fleetIdFromUrl,
    queryKey,
    queryFn,
    cacheTime: Infinity,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  return query;
};

export const hasFleetOverride = () => {
  const fleetOverride = queryClient.getQueryData<string | null>(['fleetOverride']);
  return !!fleetOverride;
};

export const setFleetOverride = (fleetUUID: string) => {
  queryClient.setQueryData(['fleetOverride'], fleetUUID);
  setQueryStringValue(FLEET_ID_QUERY_PARAM_KEY, fleetUUID, false);
};

export const getCurrentFleetID = (): string | null => {
  const fleetOverride = queryClient.getQueryData<string | null>(['fleetOverride']);
  return fleetOverride!;
};

export const useCurrentFleet = () => {
  const fleetOverride = useFleetOverride();
  const fleet = useFleetById(fleetOverride.data ?? '');

  return fleet;
};

export const setFleetData = (fleet: Fleet) => {
  queryClient.setQueryData(['fleet', fleet.uuid, 'details'], fleet);
};

export const getCurrentFleet = (): Fleet | null => {
  const fleetOverride = getCurrentFleetID();
  const fleet = queryClient.getQueryData<Fleet>(['fleet', fleetOverride ?? '', 'details']);
  return fleet ?? null;
};
